import type { ContactFormValues } from '@common/types/formSubmissions';
import type { TemplateFormField } from '@common/types/templateFormFields';
import type { TemplateFormLanguage } from '@common/types/templateForms';
import { parseMultiSelectValue } from '@common/utils/intakeFieldFormatting';
import { isRequiredTableValueEmpty } from '@/components/FormComponent/tableValues';

/** True for null, undefined, or empty/whitespace-only strings — common empty form field values. */
export function isEmptyFormValue(value: unknown): boolean {
    if (value == null) return true;
    if (typeof value === 'string') return value.trim() === '';
    return false;
}

/** Normalize field value for FormComponent without mutating store state during render. */
export function getFormFieldDisplayValue(field: TemplateFormField) {
    if (field.questionSchema?.type === 'date' && isEmptyFormValue(field.questionSchema?.value)) {
        return undefined;
    }

    return field.questionSchema.value;
}

export function isEmptyRequiredFieldValue(
    value: unknown,
    fieldType: string | undefined,
    columns?: TemplateFormField['questionSchema']['columns'],
    rowCount?: TemplateFormField['questionSchema']['rowCount'],
): boolean {
    if (fieldType === 'table') {
        return isRequiredTableValueEmpty(
            typeof value === 'string' ? value : value == null ? undefined : String(value),
            columns,
            rowCount,
        );
    }

    if (fieldType === 'multiselect') {
        return parseMultiSelectValue(value as string | string[] | null | undefined).length === 0;
    }

    return isEmptyFormValue(value);
}

/**
 * Discriminated union representing the different ways a user can enter the intake form.
 *
 * - `fresh`: User navigates directly to the intake link. We know nothing about them.
 * - `manualProspect`: Law firm sent an email with the intake link to a specific client.
 *   A prospect already exists and tracks the deal from email -> quote -> intake.
 * - `draft`: User is continuing from a previously saved draft.
 * - `postIntake`: User is editing an already-submitted intake form.
 */
export type FormEntryMode =
    | { type: 'fresh' }
    | {
          type: 'manualProspect';
          prospectId: string;
          instanceId: string;
          prospectCreatedAs: string | null;
      }
    | {
          type: 'draft';
          draftFormSubmissionId: string;
          prospectId: string | null;
          prospectCreatedAs: string | null;
      }
    | {
          type: 'postIntake';
          formSubmissionId: string;
          prospectVersionId: number;
          prospectId: string | null;
      };

/**
 * Derive the FormEntryMode from URL search params.
 * The order of checks matters - more specific modes (with more params) are checked first.
 */
export function deriveFormEntryMode(searchParams: URLSearchParams): FormEntryMode {
    const prospectId = searchParams.get('prospectId');
    const instanceId = searchParams.get('instanceId');
    const draftFormSubmissionId = searchParams.get('draftFormSubmissionId');
    const formSubmissionId = searchParams.get('formSubmissionId');
    const prospectVersionId = searchParams.get('prospectVersionId');
    const prospectCreatedAs = searchParams.get('prospectCreatedAs');

    // Post-intake edit: has formSubmissionId + prospectVersionId
    if (formSubmissionId && prospectVersionId) {
        return {
            type: 'postIntake',
            formSubmissionId,
            prospectVersionId: Number(prospectVersionId),
            prospectId,
        };
    }

    // Draft continuation: has draftFormSubmissionId
    if (draftFormSubmissionId) {
        return {
            type: 'draft',
            draftFormSubmissionId,
            prospectId,
            prospectCreatedAs,
        };
    }

    // Manual prospect: has prospectId + instanceId (law firm sent intake link)
    if (prospectId && instanceId) {
        return {
            type: 'manualProspect',
            prospectId,
            instanceId,
            prospectCreatedAs,
        };
    }

    // Fresh: user navigated directly, we know nothing about them
    return { type: 'fresh' };
}

/**
 * Type guard helpers for narrowing FormEntryMode
 */
export function isManualProspect(
    mode: FormEntryMode,
): mode is Extract<FormEntryMode, { type: 'manualProspect' }> {
    return mode.type === 'manualProspect';
}

export function isDraftMode(
    mode: FormEntryMode,
): mode is Extract<FormEntryMode, { type: 'draft' }> {
    return mode.type === 'draft';
}

export function isPostIntakeMode(
    mode: FormEntryMode,
): mode is Extract<FormEntryMode, { type: 'postIntake' }> {
    return mode.type === 'postIntake';
}

export function isFreshMode(
    mode: FormEntryMode,
): mode is Extract<FormEntryMode, { type: 'fresh' }> {
    return mode.type === 'fresh';
}

/**
 * Helper to get prospectId from any mode that has one
 */
export function getProspectId(mode: FormEntryMode): string | null {
    switch (mode.type) {
        case 'manualProspect':
            return mode.prospectId;
        case 'draft':
        case 'postIntake':
            return mode.prospectId;
        case 'fresh':
            return null;
    }
}

/**
 * Helper to check if this mode requires email verification before showing the form
 */
export function requiresEmailVerification(mode: FormEntryMode): boolean {
    return mode.type !== 'fresh';
}

/**
 * Extracts API error information from an unknown error.
 * Handles both Axios errors and generic errors with response-like shapes.
 */
export interface ApiErrorData {
    Status?: string;
    Message?: string;
    Language?: TemplateFormLanguage;
    DraftFormSubmissionId?: string;
}

export interface ApiErrorInfo {
    status: number | null;
    statusText: string | null;
    data: ApiErrorData | string | null;
}

export function extractApiError(error: unknown): ApiErrorInfo {
    if (typeof error !== 'object' || error === null) {
        return { status: null, statusText: null, data: null };
    }

    const err = error as Record<string, unknown>;

    // Handle axios-style errors with response property
    if ('response' in err && typeof err.response === 'object' && err.response !== null) {
        const response = err.response as Record<string, unknown>;
        let data: ApiErrorInfo['data'] = null;
        if (typeof response.data === 'string') {
            data = response.data;
        } else if (typeof response.data === 'object' && response.data !== null) {
            data = response.data as ApiErrorData;
        }
        return {
            status: typeof response.status === 'number' ? response.status : null,
            statusText: typeof response.statusText === 'string' ? response.statusText : null,
            data,
        };
    }

    // Handle errors with status directly on the error object (some axios configs)
    if ('status' in err) {
        return {
            status: typeof err.status === 'number' ? err.status : null,
            statusText: null,
            data: null,
        };
    }

    return { status: null, statusText: null, data: null };
}

/**
 * Helper to safely get Status/Message from ApiErrorInfo.data
 * (handles both object and string data)
 */
export function getApiErrorStatus(info: ApiErrorInfo): string | undefined {
    return typeof info.data === 'object' && info.data !== null ? info.data.Status : undefined;
}

export function getApiErrorLanguage(info: ApiErrorInfo): TemplateFormLanguage | undefined {
    return typeof info.data === 'object' && info.data !== null ? info.data.Language : undefined;
}

export function getApiErrorMessage(info: ApiErrorInfo): string {
    const defaultValue = 'Something went wrong!';
    if (typeof info.data === 'string') {
        return info.data;
    }
    if (typeof info.data === 'object' && info.data !== null) {
        return info.data.Message ?? defaultValue;
    }
    return defaultValue;
}

/**
 * Groups template form fields by their formGroupTitle after parsing JSON schemas.
 * Fields are sorted by displayOrder within each group.
 */
export function groupTemplateFormFields(
    fields: TemplateFormField[],
): Record<string, TemplateFormField[]> {
    const grouped: Record<string, TemplateFormField[]> = {};

    for (const field of fields) {
        const groupKey = field.formGroupTitle ?? '';

        if (!grouped[groupKey]) {
            grouped[groupKey] = [];
        }
        grouped[groupKey].push(field);
    }

    // Sort fields within each group by displayOrder
    for (const groupKey of Object.keys(grouped)) {
        grouped[groupKey].sort((a, b) => a.displayOrder - b.displayOrder);
    }

    return grouped;
}

/**
 * Checks if a group has any visible fields.
 */
export function hasVisibleFields(fields?: TemplateFormField[]): boolean {
    return !!fields?.some((field) => field.isVisibleToUser);
}

/**
 * Sets an error for a specific field.
 * Returns a new FieldErrors object (immutable update).
 */
export function setFieldError(
    errors: Record<string, string | null>,
    fieldId: string,
    message: string | null,
): Record<string, string | null> {
    return { ...errors, [fieldId]: message };
}

/**
 * Clears all field errors.
 * Returns a new FieldErrors object with all values set to null.
 */
export function clearAllFieldErrors(
    errors: Record<string, string | null>,
): Record<string, string | null> {
    const cleared: Record<string, string | null> = {};
    for (const fieldId of Object.keys(errors)) {
        cleared[fieldId] = null;
    }
    return cleared;
}

/**
 * Checks if there are any active field errors.
 */
export function hasFieldErrors(errors: Record<string, string | null>): boolean {
    return Object.values(errors).some((error) => error !== null);
}

/**
 * The structure expected by the submission API for each field.
 */
export interface TemplateFormFieldSubmission {
    formFieldId: string;
    formFieldResponse: string | null;
}

/**
 * Converts form values state into the array format expected by the submission API.
 *
 * @param formValues - The current form values keyed by templateFormFieldId
 * @param groupedFields - The grouped fields to determine which fields to include
 * @returns Array of field submissions ready for the API payload
 */
export function buildTemplateFormFields(
    formValues: Record<string, string | null>,
    groupedFields: Record<string, TemplateFormField[]>,
): TemplateFormFieldSubmission[] {
    return Object.values(groupedFields)
        .flat()
        .filter((field) => field.templateFormFieldId in formValues)
        .map((field) => ({
            formFieldId: field.templateFormFieldId,
            formFieldResponse: formValues[field.templateFormFieldId] ?? null,
        }));
}

/**
 * Initializes form values from draft form response data.
 * Draft responses use PascalCase property names (FormFieldId, FormFieldResponse).
 *
 * @param draftFields - Array of draft form response fields
 * @returns FormValues object keyed by templateFormFieldId
 */
export function initFormValuesFromDraft(
    draftFields: Array<{ FormFieldId: string; FormFieldResponse: string | null }>,
): Record<string, string | null> {
    const values: Record<string, string | null> = {};
    for (const field of draftFields) {
        values[field.FormFieldId] = field.FormFieldResponse;
    }
    return values;
}

/**
 * Initializes form values from post-intake form submission data.
 * Post-intake responses use camelCase property names (formFieldId, formFieldResponse).
 *
 * @param submissionFields - Array of form submission fields
 * @returns FormValues object keyed by templateFormFieldId
 */
export function initFormValuesFromSubmission(
    submissionFields: Array<{ formFieldId: string; formFieldResponse: string | null }>,
): Record<string, string | null> {
    const values: Record<string, string | null> = {};
    for (const field of submissionFields) {
        values[field.formFieldId] = field.formFieldResponse;
    }
    return values;
}

/**
 * Updates a single field value in the form values state.
 * Returns a new object (immutable update).
 *
 * @param values - Current form values
 * @param fieldId - The templateFormFieldId to update
 * @param value - The new value (will be converted to string or null)
 */
export function setFormValue(
    values: Record<string, string | null>,
    fieldId: string,
    value: unknown,
): Record<string, string | null> {
    return { ...values, [fieldId]: toFormValueString(value) };
}

/** The string encoding used for every stored answer, top-level or nested in a contact block. */
export function toFormValueString(value: unknown): string | null {
    return value === null || value === undefined || value === '' ? null : String(value);
}

/**
 * Converts UI ContactFormValues to API format.
 * Normalizes empty strings to undefined for optional fields.
 */
export function toContactFormValuesApi(values: ContactFormValues): ContactFormValues {
    return {
        // Which party the values belong to, so the backend saves them onto that party
        contactId: values.contactId || undefined,
        // ID document fields
        firstIdType: values.firstIdType || undefined,
        secondIdType: values.secondIdType || undefined,
        firstIdFront: values.firstIdFront || undefined,
        firstIdBack: values.firstIdBack || undefined,
        firstIdSingle: values.firstIdSingle || undefined,
        secondIdFront: values.secondIdFront || undefined,
        secondIdBack: values.secondIdBack || undefined,
        secondIdSingle: values.secondIdSingle || undefined,
        // Required contact fields
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        cellPhone: values.cellPhone || undefined,
        // Optional contact fields
        middleName: values.middleName || undefined,
        homePhone: values.homePhone || undefined,
        homeAddress: values.homeAddress || undefined,
        homeUnit: values.homeUnit || undefined,
        dateOfBirth: values.dateOfBirth || undefined,
        gender: values.gender || undefined,
        preferredPronouns: values.preferredPronouns || undefined,
        maritalStatus: values.maritalStatus || undefined,
        citizenshipStatus: values.citizenshipStatus || undefined,
        occupation: values.occupation || undefined,
        employer: values.employer || undefined,
        employerAddress: values.employerAddress || undefined,
        employerUnit: values.employerUnit || undefined,
        employerPhone: values.employerPhone || undefined,
    };
}
